"use client";

import { isFieldRequired, VALUATION_CURRENCIES } from "@repo/questionnaire";
import { IntakeBusinessTypeField } from "@/components/intake/intake-business-type-field";
import type { BusinessTypeOption } from "@/components/listings/business-type-combobox";
import { Input } from "@/components/ui/input";

import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { EarningsInput } from "./earnings-input";
import { DurationField } from "./duration-field";

import type { QuestionnaireFieldRendererProps } from "./questionnaire-field-renderer-types";
const COMFORTABLE_CONTROL = "h-12 text-base md:text-base";
export function QuestionnaireBasicControl({
  field,
  answers = {},
  value,
  onChange,
  disabled,
  error,
  translations,
  locale,
  defaultCountry,
}: QuestionnaireFieldRendererProps) {
  const accessibility = {
    "aria-labelledby": `${field.id}-label`,
    "aria-required": isFieldRequired(field, answers),
    "aria-invalid": Boolean(error),
    "aria-describedby": error ? `${field.id}-error` : undefined,
  };
  if (field.source?.reference === "valuation.earnings")
    return (
      <EarningsInput
        id={field.id}
        currency={
          VALUATION_CURRENCIES[
            String(
              answers.country ?? defaultCountry ?? "us",
            ).toLowerCase() as keyof typeof VALUATION_CURRENCIES
          ] ?? "USD"
        }
        value={value}
        disabled={disabled}
        error={error}
        onChange={onChange}
      />
    );
  if (field.kind === "page") return null;
  if (field.kind === "text" || field.kind === "date") {
    return (
      <Input
        {...accessibility}
        id={field.id}
        value={typeof value === "string" ? value : ""}
        type={
          field.kind === "date"
            ? "date"
            : field.source?.reference === "contact.email"
              ? "email"
              : "text"
        }
        minLength={field.minLength}
        maxLength={field.maxLength}
        placeholder={field.placeholder}
        disabled={disabled}
        className={COMFORTABLE_CONTROL}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }
  if (field.kind === "long_text") {
    return (
      <Textarea
        {...accessibility}
        id={field.id}
        value={typeof value === "string" ? value : ""}
        minLength={field.minLength}
        maxLength={field.maxLength}
        placeholder={field.placeholder}
        disabled={disabled}
        className={cn("min-h-24 text-base md:text-base", COMFORTABLE_CONTROL)}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }
  if (field.kind === "duration") {
    return (
      <DurationField
        id={field.id}
        value={value}
        disabled={disabled}
        locale={locale}
        translations={translations}
        onChange={onChange}
      />
    );
  }
  if (field.kind === "business_type") {
    return (
      <IntakeBusinessTypeField
        searchable={field.source?.reference === "valuation.businessType"}
        id={field.id}
        label={field.label}
        error={error}
        translations={translations}
        locale={locale}
        value={
          typeof value === "string"
            ? value
            : value && typeof value === "object"
              ? (value as BusinessTypeOption)
              : null
        }
        disabled={disabled}
        onChange={onChange}
      />
    );
  }
  return null;
}
